import { getClauseKind } from '@gonfalon/clauses/getClauseKind';
import { hasSegmentOp } from '@gonfalon/clauses/hasSegmentOp';
import type { ClauseValue } from '@gonfalon/clauses/types';
import { updateClauseAttribute } from '@gonfalon/clauses/updateClauseAttribute';
import { updateClauseContextKind } from '@gonfalon/clauses/updateClauseContextKind';
import { updateClauseOperator } from '@gonfalon/clauses/updateClauseOperator';
import { ContextKindKey } from '@gonfalon/contexts/types';
import { fromJS, List, Record } from '@gonfalon/immutable-shim/immutableShim';
import type { CreateFunctionInput } from '@gonfalon/legacy-immutable/immutableUtils';
import nullthrows from 'nullthrows';
import { v4 } from 'uuid';

import { type ClauseProps } from '../types';

export class Clause extends Record<Omit<ClauseProps, 'values'> & { values: List<ClauseValue> }>({
  _key: '',
  _id: '',
  attribute: '',
  op: '',
  contextKind: '',
  values: List(),
  negate: false,
}) {
  toRep() {
    return fromJS(this.toJSON()).withMutations((map: Clause) => {
      map.remove('_key');
    });
  }

  updateValues(values: ClauseValue[]) {
    return this.set('values', List(values));
  }

  updateAttribute(attr: string) {
    const updatedClause = updateClauseAttribute(this.toJS(), attr);
    return this.withMutations((c) => {
      c.set('attribute', updatedClause.attribute)
        .set('op', updatedClause.op)
        .set('negate', updatedClause.negate)
        .set('values', List(updatedClause.values as ClauseValue[]));
    });
  }

  updateContextKind(contextKind: string = '') {
    const updatedClause = updateClauseContextKind(this.toJS(), contextKind);
    return this.set('contextKind', nullthrows(updatedClause.contextKind));
  }

  updateOperator(op: string, negate: boolean) {
    const updatedClause = updateClauseOperator(this.toJS(), op, negate);
    return this.merge({
      op: updatedClause.op,
      negate: updatedClause.negate,
      values: List(updatedClause.values as ClauseValue[]),
    });
  }

  hasSegmentOp() {
    return hasSegmentOp(this.toJS());
  }

  hasSegment(segmentKey: string) {
    return this.hasSegmentOp() && this.values.includes(segmentKey);
  }

  getContextKind() {
    return this.contextKind || ContextKindKey.USER;
  }

  getClauseKind() {
    return getClauseKind(this.toJS());
  }
}

export function createClause(props: CreateFunctionInput<Clause> = {}) {
  const clause = props instanceof Clause ? props : new Clause(fromJS(props));
  return clause.update('_key', (key) => key || v4()).update('values', (values) => values || List());
}
